/**
 * Sidebar module data hook. The two `useQuery` calls live in the shared
 * `useWorkspaceModuleQueries` hook so the wide sidebar, the rail, and the
 * AuthenticatedAppShell readiness gate cannot drift on query identity.
 */

import { useMemo } from 'react';
import { buildSidebarModuleItems, sortSidebarModules } from '../moduleMapping';
import type { SidebarModuleItem } from '../types';
import { useWorkspaceModuleQueries } from './useWorkspaceModuleQueries';

export function useSidebarModulesData(token: string | null | undefined, workspaceId: string) {
  const { modulesQuery, preferencesQuery } = useWorkspaceModuleQueries(token, workspaceId);
  const workspaceModules = modulesQuery.data ?? [];
  const moduleSidebarPreferences = preferencesQuery.data ?? [];

  const modulePreferenceById = useMemo(
    () => new Map(moduleSidebarPreferences.map((preference) => [preference.moduleId, preference])),
    [moduleSidebarPreferences],
  );

  const sidebarModuleItems = useMemo<SidebarModuleItem[]>(
    () => buildSidebarModuleItems(workspaceModules, modulePreferenceById),
    [workspaceModules, modulePreferenceById],
  );

  // Match chat pin behavior: pinned modules first, then unpinned modules.
  // Within pinned modules, respect explicit pinOrder if present.
  // Within each bucket, keep module position ordering (then alphabetical fallback).
  const allModulesSorted = useMemo(
    () => sortSidebarModules(sidebarModuleItems),
    [sidebarModuleItems],
  );

  return {
    workspaceModules,
    moduleSidebarPreferences,
    modulePreferenceById,
    sidebarModuleItems,
    allModulesSorted,
  };
}
